import type { ResourceTableDescriptor, TranslateFn } from '@/components/ui/embedded-resource-table/types';
import type { ToolbarFilterOption } from '@/components/ui/generic-toolbar/GenericToolbar';
import { buildAssignedUserOptions, type AssignableUser } from '@/lib/assigned-user-options';
import { settingLabel } from '@/lib/status-labels';
import type { Lead } from '@/types';

/** The person on a lead, in the order the table and the filters both resolve it. */
export function leadOwner(lead: Lead) {
    return lead.lead_owner || lead.assigned_user || lead.user;
}

function ownerName(lead: Lead): string {
    const owner = leadOwner(lead);

    return owner ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim() : '';
}

function createdYear(lead: Lead): string {
    return lead.created_at ? String(new Date(lead.created_at).getFullYear()) : '';
}

/**
 * The one description of the leads table.
 *
 * `pages/leads/index.tsx` and every detail-page tab that lists leads read their
 * columns, filters and sort entries from here, so the same list cannot look
 * different in three places. `LeadTable` derives its own catalogue from it too.
 */
export const leadTableDescriptor: ResourceTableDescriptor<Lead> = {
    key: 'leads',
    resourceName: 'lead',
    resourceNamePlural: 'leads',
    getRowId: (lead) => lead.id,

    columns: [
        {
            id: 'lead_number',
            labelKey: 'leads.tables.lead_number',
            labelFallback: 'Lead Number',
            sort: { serverKey: 'lead_number', value: (lead) => lead.lead_number ?? lead.id },
        },
        {
            id: 'name',
            labelKey: 'leads.tables.object_name',
            labelFallback: 'Object name',
            sort: { serverKey: 'name', value: (lead) => lead.name ?? '' },
        },
        {
            id: 'estimated_value',
            labelKey: 'leads.tables.estimated_value',
            labelFallback: 'Estimated Value',
            sort: { serverKey: 'estimated_value', value: (lead) => lead.estimated_value ?? lead.value ?? 0 },
        },
        {
            id: 'offer_due_date',
            labelKey: 'leads.tables.offer_due_date',
            labelFallback: 'Offer deadline',
            sort: { serverKey: 'offer_due_date', value: (lead) => lead.offer_due_date ?? '' },
        },
        {
            id: 'company',
            labelKey: 'leads.tables.company',
            labelFallback: 'Company',
            sort: { serverKey: 'company', value: (lead) => lead.company?.name ?? '' },
        },
        {
            id: 'status',
            labelKey: 'leads.tables.status',
            labelFallback: 'Status',
            sort: { serverKey: 'status', value: (lead) => String(lead.status) },
        },
        {
            id: 'priority',
            labelKey: 'leads.tables.priority',
            labelFallback: 'Priority',
            sort: { serverKey: 'priority', value: (lead) => String(lead.priority) },
        },
        // Last and compact, matching the projects table: the person on a record is
        // an avatar at the end of the row, not a name column in the middle.
        {
            id: 'lead_owner',
            labelKey: 'leads.tables.lead_owner',
            labelFallback: 'Lead Owner',
            compact: true,
            sort: { serverKey: 'lead_owner', value: ownerName },
        },
    ],

    /** The object name sits next to the number: the number identifies a lead, the name says what it is. */
    defaultVisibleColumns: ['lead_number', 'name', 'company', 'estimated_value', 'offer_due_date', 'status', 'priority', 'lead_owner'],

    filters: [
        {
            id: 'created_year',
            param: 'created_year',
            labelKey: 'leads.index.filters.year',
            labelFallback: 'Created year',
            matches: (lead, values) => values.includes(createdYear(lead)),
        },
        {
            id: 'status',
            param: 'status',
            labelKey: 'leads.index.quick_filters.status',
            labelFallback: 'Status',
            matches: (lead, values) => values.includes(String(lead.status)),
        },
        {
            id: 'priority',
            param: 'priority',
            labelKey: 'leads.index.quick_filters.priority',
            labelFallback: 'Priority',
            matches: (lead, values) => values.includes(String(lead.priority)),
        },
        {
            id: 'company',
            param: 'company_id',
            labelKey: 'leads.tables.company',
            labelFallback: 'Company',
            matches: (lead, values) => values.includes(lead.company?.id ?? lead.company_id ?? ''),
        },
        {
            // The table column is `lead_owner`; the server reads `owner`.
            id: 'lead_owner',
            param: 'owner',
            labelKey: 'leads.tables.lead_owner',
            labelFallback: 'Lead Owner',
            matches: (lead, values) => {
                const owner = leadOwner(lead);

                return owner ? values.includes(owner.id) : false;
            },
        },
    ],

    // The created year leads: it is the one people narrow by first.
    quickFilterIds: ['created_year', 'status', 'priority'],

    searchValues: (lead) => [lead.lead_number, lead.name, lead.company?.name, ownerName(lead)],

    allLabel: { labelKey: 'leads.index.filters.all_leads', labelFallback: 'All Leads' },
    quickFiltersLabel: { labelKey: 'leads.index.toolbar.quick_filters', labelFallback: 'Quick filters' },
};

/**
 * Where each filter's options come from.
 *
 * Kept out of the descriptor because the sources genuinely differ by page — an
 * index page has the server's reference lists, a detail tab may only have the
 * rows — while the labelling, ordering and grouping must not.
 */
export interface LeadFilterOptionSources {
    t: TranslateFn;
    statuses: Array<{ value: string }>;
    priorities: Array<{ value: string }>;
    owners: AssignableUser[];
    companies: Array<{ id: string; name: string }>;
    years?: string[];
}

export function leadFilterOptions({
    t,
    statuses,
    priorities,
    owners,
    companies,
    years = [],
}: LeadFilterOptionSources): Record<string, ToolbarFilterOption[]> {
    return {
        // The labels the backend sends come from config.json and are English only,
        // so route the slugs through the same `settings.*` keys the rest of the UI
        // uses rather than showing them raw.
        status: statuses.map((status) => ({ value: status.value, label: settingLabel(t, 'lead_statuses', status.value) })),
        priority: priorities.map((priority) => ({ value: priority.value, label: settingLabel(t, 'lead_priorities', priority.value) })),
        lead_owner: buildAssignedUserOptions(owners, t),
        company: companies.map((company) => ({ value: company.id, label: company.name })).sort((a, b) => a.label.localeCompare(b.label)),
        created_year: years.map((year) => ({ value: year, label: year })),
    };
}

/**
 * Fallback sources for a tab that was handed rows but no reference lists.
 *
 * Row-derived options can only ever offer values already on screen, so prefer
 * passing the server's lists; this exists so a page can be migrated before its
 * controller is.
 */
export function leadOptionSourcesFromRows(leads: Lead[]): Pick<LeadFilterOptionSources, 'owners' | 'companies'> {
    const owners = new Map<string, AssignableUser>();
    const companies = new Map<string, { id: string; name: string }>();

    leads.forEach((lead) => {
        const owner = leadOwner(lead);
        if (owner) {
            owners.set(owner.id, owner as AssignableUser);
        }
        if (lead.company?.id && lead.company?.name) {
            companies.set(lead.company.id, { id: lead.company.id, name: lead.company.name });
        }
    });

    return { owners: [...owners.values()], companies: [...companies.values()] };
}
